import { useMemo, useRef, useState, type ReactNode } from 'react';
import { drawElements } from '../../elements/layout_elements.ts';
import { CanvasView } from '../../view/canvas_view.tsx';
import { useLegacyIcon } from '../../view/icons.ts';
import { TILE_FONT } from '../../view/theme.ts';
import { useResolvedTileStyle, useTileTextColor } from '../../view/tile_style.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { useTween } from '../../view/tween.ts';
import { DEFAULT_VALUE_COLOR, GAUGE_MOVE_MS, formatTileNumber, gaugeValues, valueTileElements, withGaugeValues } from '../value/value_tile.ts';
import { SetpointPicker } from './setpoint_picker.tsx';
import {
    byValuesOf,
    modeLabel,
    numberFormatsOf,
    sentReadings,
    separateData,
    setpointRange,
    sourceNamesOf,
    startingSetpoint,
    stateIconOf,
    thermostatData,
    thermostatTileContent,
    type ThermostatTile,
} from './thermostat_tile.ts';

/** The thermostat role's commands (device-roles.md DR-6). */
const SET_SETPOINT = 'set_setpoint';
const SET_MODE = 'set_mode';

/**
 * The content area of a `thermostat` tile (`thermostatTileContent`), with its live temperature, set
 * point, mode and state. A tap opens the set-point picker; a value set there is shown at once and
 * sent to the set point's driver. A mode chosen there is sent at once (device-roles.md TH-4).
 */
export function ThermostatTileView({ tile }: { tile: ThermostatTile }) : ReactNode {
    const byValues = byValuesOf(tile.options);
    // The same hooks either way, in the same order: a thermostat is one source for all three.
    const names = sourceNamesOf(tile.options);
    const environment = useTileEnvironment();
    const [temperatureReadings, setpointReadings, modeReadings] = environment.useReadingsList(tile, names);
    const sendSetpoint = environment.useCommand(tile, names[1]);
    const sendMode = environment.useCommand(tile, names[2]);
    const setpointCommands = environment.useCommands(tile, names[1]);
    const modeCommands = environment.useCommands(tile, names[2]);

    const data = byValues ? separateData(temperatureReadings, setpointReadings, modeReadings) : thermostatData(setpointReadings);
    const setpointCommand = byValues?.setpoint.command ?? SET_SETPOINT;
    const modeCommand = byValues ? byValues.mode?.command : SET_MODE;
    const range = useMemo(() => setpointRange(setpointCommands?.find((command) => command.name === setpointCommand)?.argument), [setpointCommands, setpointCommand]);
    const modeValues = modeCommands?.find((command) => command.name === modeCommand)?.argument?.values ?? [];
    const modes = modeValues.map((each) => ({ value: String(each.value), label: each.label ?? modeLabel(String(each.value)) }));

    const textColor = useTileTextColor(DEFAULT_VALUE_COLOR);
    const stateIcon = useLegacyIcon(stateIconOf(data.mode, data.state)?.name);
    const look = { stateIcon };
    const content = thermostatTileContent(tile, data, environment.locale, textColor, useResolvedTileStyle().gaugeStyle, look);
    // The dial moves to a new temperature or set point (docs/specs/gauge-styles.md §7).
    const moving = useTween(gaugeValues(content), GAUGE_MOVE_MS);
    // The set point's unit in the picker; a degree sign where the tile shows none.
    const unit = byValues && byValues.setpoint.unit !== '' ? byValues.setpoint.unit : '°';
    const setpointFormat = numberFormatsOf(tile.options).setpoint;
    const area = useRef<HTMLDivElement>(null);
    const [anchor, setAnchor] = useState<DOMRect | null>(null);

    const warn = (command: string, value: unknown) => (error: unknown) : void => {
        // The tile shows the failure with its warning mark; this is for the console.
        console.warn(`${tile.label ?? 'thermostat'}: ${command} ${String(value)}: ${error instanceof Error ? error.message : String(error)}`);
    };
    const pick = (choice: number) : void => {
        setAnchor(null);
        sendSetpoint(setpointCommand, choice, sentReadings(setpointReadings, !byValues, 'setpoint', choice)).catch(warn(setpointCommand, choice));
    };
    const chooseMode = (choice: string) : void => {
        if (modeCommand !== undefined) {
            sendMode(modeCommand, choice, sentReadings(modeReadings, !byValues, 'mode', choice)).catch(warn(modeCommand, choice));
        }
    };

    return (
        <div ref={area} style={{ position: 'absolute', inset: 0, cursor: 'pointer' }} onClick={() => setAnchor(area.current?.closest('[data-tile]')?.getBoundingClientRect() ?? area.current?.getBoundingClientRect() ?? null)}>
            <CanvasView
                // Everything drawn, so that a change in the editor (the gauge's colours) draws it again.
                deps={[JSON.stringify(content), stateIcon]}
                animate={moving.isMoving}
                draw={(ctx, width, height, now) => drawElements(ctx, { left: 0, top: 0, width, height }, valueTileElements(withGaugeValues(content, moving.at(now)), TILE_FONT))}
            />
            {anchor && (
                <SetpointPicker
                    anchor={anchor}
                    title={tile.label ?? 'Thermostat'}
                    start={startingSetpoint(data.setpoint, data.temperature, range)}
                    current={data.setpoint}
                    nowText={data.temperature === null ? '' : `${content.numberText}${unit}`}
                    format={(value) => formatTileNumber(value, setpointFormat, environment.locale)}
                    unit={unit}
                    range={range}
                    modes={modes}
                    mode={data.mode}
                    onMode={modeCommand === undefined ? undefined : chooseMode}
                    onSet={pick}
                    onClose={() => setAnchor(null)}
                />
            )}
        </div>
    );
}
